import { type ReactNode, useMemo } from 'react'
import cx from 'classnames'
import styles from './AssignFileTypeDialog.module.css'
import { GridList, GridListItem } from 'react-aria-components'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { Button, RawButton } from '@/components/Button'
import { isNotNil } from '@/utils/object-util'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { AutoSkeleton, useIsSkeleton } from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { splitFileName } from '@/utils/file-util'

export interface AssignFileTypeDialogProps {
  children?: ReactNode
  isOpen: boolean
  onClose?: () => unknown
  onUpload?: () => unknown
  isUploadDisabled?: boolean
  testId?: string
  uploadButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * A dialog for assigning file types to documents being uploaded.
 */
export function AssignFileTypeDialog({
  children,
  isOpen,
  onClose,
  onUpload,
  testId,
  uploadButtonTestId,
  cancelButtonTestId,
}: AssignFileTypeDialogProps) {
  return (
    <ModalDialog isOpen={isOpen} onClose={onClose} size="lg" testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>Assign file type</ModalDialogTitle>
        <ModalDialogDescription>
          Select an appropriate document type for each file.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <GridList aria-label="Files" className={styles.entries}>
          {children}
        </GridList>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Upload"
            onPress={onUpload}
            testId={uploadButtonTestId}
          />
          <Button
            label="Cancel"
            onPress={onClose}
            variant="outline"
            testId={cancelButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface AssignFileTypeEntryProps {
  /**
   * ID of the currently selected type.
   */
  value?: string | null

  /**
   * Document type options.
   */
  options: AssignFileTypeSelectOption[]

  /**
   * The file name of the file being uploaded.
   */
  fileName: string

  /**
   * Whether to render a skeleton.
   */
  isSkeleton?: boolean

  /**
   * Callback for when the user wishes to remove the staged file.
   */
  onRemove?: () => unknown

  /**
   * Called when a type has been selected.
   *
   * @param newId
   */
  onChange?: (newId: string) => unknown
}

/**
 * Options for {@link AssignFileTypeEntryProps.options}.
 */
export interface AssignFileTypeSelectOption {
  id: string
  displayName: string
}

/**
 * An entry for assigning a file type to a file being uploaded.
 */
export function AssignFileTypeEntry({
  fileName,
  options,
  value,
  onRemove,
  onChange,
  isSkeleton,
}: AssignFileTypeEntryProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  const { vary } = useSkeletonListContext()

  const isSpacious = useIsSpacious()

  // Extract the stem and extension from the file name
  const [stem, extension] = useMemo(() => {
    return splitFileName(fileName)
  }, [fileName])

  let valueText = 'Select file type'
  const items = options.map((option) => {
    if (option.id === value) {
      // This is safe.
      // eslint-disable-next-line react-compiler/react-compiler
      valueText = option.displayName
    }

    return (
      <DropdownItem
        key={option.id}
        textValue={option.displayName}
        onAction={() => onChange?.(option.id)}
        id={option.id}
      >
        {option.displayName}
      </DropdownItem>
    )
  })

  const selectedKeys = useMemo(
    () => new Set(isNotNil(value) ? [value] : []),
    [value],
  )

  return (
    <GridListItem
      textValue={fileName}
      className={({ isFocusVisible }) =>
        cx(styles.entry, isFocusVisible && styles.focusVisible)
      }
    >
      {onRemove && (
        <div className={styles.closeContainer}>
          <IconButton
            name="close"
            isSkeleton={isSkeleton}
            variant="secondary"
            onPress={onRemove}
            label="Remove"
          />
        </div>
      )}

      <Dropdown
        alwaysRenderTrigger
        title="Select file type"
        selectedKeys={selectedKeys}
        selectionMode="single"
        onSelectionChange={(selection) => {
          if (!(selection instanceof Set)) {
            return
          }

          const selected = selection.values().next().value
          if (!isNotNil(selected)) {
            return
          }

          onChange?.(selected)
        }}
        trigger={
          <RawButton
            isDisabled={isSkeleton}
            className={({ isFocusVisible, isHovered, isDisabled, isPressed }) =>
              cx(
                styles.button,
                isFocusVisible && styles.focusVisible,
                isDisabled && styles.disabled,
                isHovered && styles.hover,
                // Intentionally using same style as hover for pressed
                isPressed && styles.hover,
              )
            }
          >
            <div className={styles.content}>
              <p className={styles.fileName}>
                <AutoSkeleton isSkeleton={isSkeleton}>
                  <span className={styles.stem}>{stem}</span>
                  {extension && (
                    <span className={styles.extension}>.{extension}</span>
                  )}
                </AutoSkeleton>
              </p>
              {!isSpacious && (
                <p className={styles.documentType}>
                  <AutoSkeleton isSkeleton={isSkeleton}>
                    {valueText}
                  </AutoSkeleton>
                </p>
              )}
            </div>
            <div className={styles.extra}>
              {isSpacious && (
                <p className={styles.documentType}>
                  <AutoSkeleton
                    //topCorrection={5}
                    isSkeleton={isSkeleton}
                    characters={vary(15)}
                  >
                    {valueText}
                  </AutoSkeleton>
                </p>
              )}
              <span className={styles.chevron}>
                <Icon
                  skeleton={isSkeleton}
                  name="chevron"
                  rotate={isSpacious ? 90 : 0}
                />
              </span>
            </div>
          </RawButton>
        }
      >
        {items}
      </Dropdown>
    </GridListItem>
  )
}
